import React, { useState, useRef, useEffect } from 'react'
import { sdk } from '@farcaster/miniapp-sdk'
import { useConnect } from 'wagmi'
import { useMintNFT } from '../hooks/useMintNFT'
import { CONTRACT_ADDRESSES } from '../config/wagmi'
import './ImageConverter.css'

const ImageConverter = ({ onImageSelect }) => {
  const [selectedImage, setSelectedImage] = useState(null)
  const [convertedImage, setConvertedImage] = useState(null)
  const [originalCloudinaryUrl, setOriginalCloudinaryUrl] = useState(null)
  const [isConverting, setIsConverting] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [activeTab, setActiveTab] = useState('original')
  const [urlCopied, setUrlCopied] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showWalletModal, setShowWalletModal] = useState(false)
  const [canvasCache, setCanvasCache] = useState(null)
  const fileInputRef = useRef(null)
  const mintSuccessRef = useRef(null)
  const mintErrorRef = useRef(null)

  // NFT minting functionality
  const { 
    mintNFT, 
    isMinting, 
    mintResult, 
    error: mintError, 
    canMint, 
    mintPrice, 
    usdcBalance, 
    isConnected,
    chainId
  } = useMintNFT()
  const { connect, connectors, isPending } = useConnect()

  // Wallet connection handlers
  const handleWalletSelection = async (isMobile) => {
    setShowWalletModal(false)
    try {
      console.log('Available connectors:', connectors.map(c => ({ id: c.id, name: c.name })))

      const preferredConnector = isMobile
        ? connectors.find(c => c.id === 'walletConnect') || connectors.find(c => c.id === 'injected') || connectors[0]
        : connectors.find(c => c.id === 'injected') || connectors[0]

      console.log('Selected connector:', { id: preferredConnector?.id, name: preferredConnector?.name })

      if (!preferredConnector) {
        throw new Error('No wallet connector available')
      }

      console.log('🔄 Starting connection with connector:', preferredConnector.id)
      const result = await connect({ connector: preferredConnector })
      console.log('✅ Connection result:', result)
    } catch (error) {
      console.error('Wallet connection failed:', error)
      alert(`Failed to connect wallet: ${error.message}. Please try again.`)
    }
  }

  // Auto-scroll to mint success section when minting is successful
  useEffect(() => {
    if (mintResult && mintResult.success && mintSuccessRef.current) {
      mintSuccessRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      })
    }
  }, [mintResult])

  // Auto-scroll to mint error section when minting fails
  useEffect(() => {
    if (mintError && mintErrorRef.current) {
      mintErrorRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      })
    }
  }, [mintError])

  // Convert image to standard format (JPG) while preserving dimensions
  const convertToStandardFormat = (base64Data, originalFile) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          // Preserve original dimensions exactly
          canvas.width = img.naturalWidth
          canvas.height = img.naturalHeight

          // Draw image with high quality
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

          // Convert to JPG with high quality (0.95 = 95% quality)
          const standardBase64 = canvas.toDataURL('image/jpeg', 0.95)

          // Create a new File object from the converted data
          canvas.toBlob((blob) => {
            const convertedFile = new File([blob], originalFile.name.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })

            resolve({
              file: convertedFile,
              url: standardBase64
            })
          }, 'image/jpeg', 0.95)

        } catch (error) {
          console.error('Canvas conversion failed:', error)
          reject(error)
        }
      }
      img.onerror = () => {
        console.error('Image load failed for format conversion')
        reject(new Error('Failed to load image for conversion'))
      }
      img.src = base64Data
    })
  }

  const handleImageSelect = async (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          console.log(`📁 Original file: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB, type: ${file.type}`)

          // Check file extension instead of MIME type
          const fileExtension = file.name.toLowerCase().split('.').pop()
          const standardExtensions = ['jpg', 'jpeg', 'png']
          const needsConversion = !standardExtensions.includes(fileExtension)

          let imageData
          if (needsConversion) {
            console.log(`🔄 Converting .${fileExtension} to JPG for backend compatibility`)
            const convertedImageData = await convertToStandardFormat(e.target.result, file)
            console.log(`✅ Converted to JPG, new size: ${(convertedImageData.file.size / 1024 / 1024).toFixed(2)}MB`)
            imageData = convertedImageData
          } else {
            console.log(`✅ Using original .${fileExtension} file (no conversion needed)`)
            imageData = {
              file,
              url: e.target.result
            }
          }

          setSelectedImage(imageData)
          setConvertedImage(null)
          setOriginalCloudinaryUrl(null)
          setCanvasCache(null) // Clear canvas cache for new image
          setIsImageLoading(false)
          setActiveTab('original')
          if (onImageSelect) {
            onImageSelect(imageData)
          }
        } catch (error) {
          console.error('Image conversion failed:', error)
          // Fallback to original file if conversion fails
          const imageData = {
            file,
            url: e.target.result
          }
          setSelectedImage(imageData)
          setConvertedImage(null)
          setOriginalCloudinaryUrl(null)
          setCanvasCache(null)
          setIsImageLoading(false)
          setActiveTab('original')
          if (onImageSelect) {
            onImageSelect(imageData)
          }
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleConvert = async () => {
    if (!selectedImage) return

    setIsConverting(true)

    try {
      console.log('🔄 Starting image conversion...')
      console.log('Selected image file:', selectedImage.file)
      
      const formData = new FormData()
      formData.append('image', selectedImage.file)
      formData.append('conversionType', 'blue-grid')

      console.log('📤 Sending request to /api/convert_python')
      const response = await fetch('/api/convert_python', {
        method: 'POST',
        body: formData
      })

      console.log('📥 Response status:', response.status)
      console.log('📥 Response headers:', response.headers)

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Backend response:', result)
        console.log('✅ Converted image URL:', result.convertedImageUrl)
        console.log('✅ Original Cloudinary URL:', result.originalImageUrl)
        
        // Store URLs and start image loading
        const imageUrl = result.convertedImageUrl
        
        if (result.originalImageUrl) {
          setOriginalCloudinaryUrl(result.originalImageUrl)
          // Update selectedImage to include Cloudinary URL for sharing
          setSelectedImage(prev => ({
            ...prev,
            cloudinaryUrl: result.originalImageUrl
          }))
        }
        
        // Set image loading state and preload the image
        setIsImageLoading(true)
        
        const img = new Image()
        img.onload = () => {
          console.log('✅ Converted image fully loaded and ready to display')
          setConvertedImage(imageUrl)
          setActiveTab('converted')
          setIsImageLoading(false)
          setIsConverting(false) // End converting state when image is fully loaded
        }
        img.onerror = () => {
          console.error('❌ Failed to load converted image')
          setConvertedImage(imageUrl) // Still set it, let user see the error
          setActiveTab('converted')
          setIsImageLoading(false)
          setIsConverting(false) // End converting state even on error
        }
        img.src = imageUrl
      } else {
        const errorText = await response.text()
        console.error('❌ Response not ok:', errorText)
        throw new Error(`Conversion failed: ${errorText}`)
      }
    } catch (error) {
      console.error('❌ Conversion error:', error)
      alert('Failed to convert image. Please try again.')
      setIsConverting(false)
      setIsImageLoading(false)
    }
    // Note: setIsConverting(false) is now handled in image onload/onerror
  }

  const handleShare = async () => {
    if (!convertedImage) {
      console.log('❌ [handleShare] No converted image available for sharing')
      return
    }

    setShowShareModal(true)
  }

  const shareToX = async () => {
    if (!convertedImage) return
    
    setShowShareModal(false)
    setIsSharing(true)
    
    // Open popup immediately on user interaction (before async operations)
    console.log('🐦 [shareToX] Opening popup window immediately...')
    const popup = window.open('about:blank', '_blank', 'width=600,height=400,scrollbars=no,resizable=no')
    let popupBlocked = false
    
    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      popupBlocked = true
      console.warn('⚠️ [shareToX] Popup blocked by browser')
    } else {
      console.log('✅ [shareToX] Popup opened successfully, preparing content...')
      // Show loading message in popup
      popup.document.write('<html><body><div style="text-align:center;padding:50px;font-family:Arial,sans-serif;"><h3>Preparing your share...</h3><p>Generating image canvas...</p></div></body></html>')
    }
    
    try {
      console.log('🐦 [shareToX] Sharing to X/Twitter')
      console.log('🔍 [shareToX] Converted image URL:', convertedImage)
      
      let canvasUrl
      
      // Check if we have cached canvas
      if (canvasCache) {
        console.log('✅ [shareToX] Using cached canvas:', canvasCache)
        canvasUrl = canvasCache
      } else {
        // Generate canvas
        console.log('🎨 [shareToX] Generating new canvas...')
        const baseUrl = window.location.origin
        const originalImageUrl = originalCloudinaryUrl || selectedImage?.url || ''
        const canvasApiUrl = `${baseUrl}/api/generate_canvas?converted=${encodeURIComponent(convertedImage)}${originalImageUrl ? `&original=${encodeURIComponent(originalImageUrl)}` : ''}`
        
        console.log('🎨 [shareToX] Canvas API URL:', canvasApiUrl)
        
        const canvasResponse = await fetch(canvasApiUrl)
        const canvasData = await canvasResponse.json()
        
        console.log('📊 [shareToX] Canvas response:', canvasData)
        
        if (!canvasData.success || !canvasData.canvasUrl) {
          console.error('❌ [shareToX] Canvas generation failed:', canvasData)
          if (!popupBlocked && popup && !popup.closed) {
            popup.close()
          }
          throw new Error(`Canvas generation failed: ${canvasData.error || 'Unknown error'}`)
        }
        
        canvasUrl = canvasData.canvasUrl
        setCanvasCache(canvasUrl) // Cache the generated canvas
        console.log('💾 [shareToX] Canvas cached for future use')
      }
      const shareText = `Just transformed my image with Base blue stripe effects using Base It!`
      
      console.log('🔗 [shareToX] Canvas URL:', canvasUrl)
      console.log('📝 [shareToX] Share text:', shareText)
      
      // Create share page URL that contains Twitter Card meta tags with canvas
      const sharePageUrl = `${window.location.origin}/api/s?canvas=${encodeURIComponent(canvasUrl)}`

      // Create Twitter intent URL with share page URL (Twitter will scrape the canvas)
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(sharePageUrl)}`
      console.log('🐦 [shareToX] Twitter URL:', twitterUrl)
      
      // Detect if we're in Farcaster environment
      const isInFarcaster = await sdk?.isInMiniApp?.()
      
      console.log('🔍 [shareToX] Environment detection:', {
        isInFarcaster,
        hasOpenUrl: typeof sdk?.actions?.openUrl === 'function',
        userAgent: navigator.userAgent.includes('Farcaster'),
        locationHref: window.location.href
      })
      
      if (isInFarcaster && sdk?.actions?.openUrl) {
        console.log('🎯 [shareToX] Using Farcaster openUrl for Twitter intent...')
        try {
          // Close popup immediately since we're using Farcaster SDK
          if (!popupBlocked && popup && !popup.closed) {
            popup.close()
          }
          
          console.log('📱 [shareToX] Calling sdk.actions.openUrl with:', twitterUrl)
          await sdk.actions.openUrl(twitterUrl)
          console.log('✅ [shareToX] Farcaster openUrl successful')
        } catch (farcasterError) {
          console.error('❌ [shareToX] Farcaster openUrl failed:', farcasterError)
          // Fall back to traditional method
          console.log('🔄 [shareToX] Falling back to traditional method...')
          fallbackToTraditional()
        }
      } else {
        console.log('🌐 [shareToX] Using traditional sharing method...')
        fallbackToTraditional()
      }
      
      function fallbackToTraditional() {
        // Redirect the pre-opened popup or use fallback
        if (!popupBlocked && popup && !popup.closed) {
          console.log('✅ [shareToX] Redirecting popup to Twitter...')
          popup.location.href = twitterUrl
        } else {
          console.warn('⚠️ [shareToX] Using fallback method...')
          // Fallback: create a temporary link and click it
          const link = document.createElement('a')
          link.href = twitterUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          console.log('🔗 [shareToX] Fallback link clicked')
          
          // If that fails too, copy URL to clipboard as last resort
          setTimeout(() => {
            navigator.clipboard.writeText(twitterUrl).then(() => {
              console.log('📋 [shareToX] Twitter URL copied to clipboard as backup')
            }).catch(err => console.log('Could not copy to clipboard:', err))
          }, 100)
        }
      }
      
    } catch (error) {
      console.error('❌ [shareToX] Share error:', error)
      console.error('❌ [shareToX] Error details:', {
        message: error.message,
        stack: error.stack,
        convertedImage: convertedImage,
        originalCloudinaryUrl: originalCloudinaryUrl
      })
      
      // Close popup if it's open
      if (!popupBlocked && popup && !popup.closed) {
        popup.close()
      }
      
      alert(`Failed to share to X: ${error.message}`)
    } finally {
      setIsSharing(false)
    }
  }

  const shareToFarcasterFromModal = async () => {
    if (!convertedImage) return
    
    setShowShareModal(false)
    setIsSharing(true)
    
    try {
      console.log('🚀 [shareToFarcasterFromModal] Share button clicked')
      console.log('🚀 [shareToFarcasterFromModal] SDK available:', !!sdk)
      console.log('🚀 [shareToFarcasterFromModal] Current URL:', window.location.href)
      console.log('🚀 [shareToFarcasterFromModal] User agent:', navigator.userAgent)
      console.log('🚀 [shareToFarcasterFromModal] Converted image URL:', convertedImage)
      console.log('🚀 [shareToFarcasterFromModal] Original image URL (base64):', selectedImage?.url ? selectedImage.url.substring(0, 50) + '...' : 'none')
      console.log('🚀 [shareToFarcasterFromModal] Original Cloudinary URL:', originalCloudinaryUrl)
      
      let canvasUrl
      
      // Check if we have cached canvas
      if (canvasCache) {
        console.log('✅ [shareToFarcasterFromModal] Using cached canvas:', canvasCache)
        canvasUrl = canvasCache
      } else {
        // Step 1: Generate canvas first
        console.log('🎨 [shareToFarcasterFromModal] Generating new canvas...')
        const baseUrl = window.location.origin
        
        // Use Cloudinary URL for original if available, otherwise fallback to base64
        const originalImageUrl = originalCloudinaryUrl || selectedImage?.url || ''
        
        // Call canvas generation API with both image URLs
        const canvasApiUrl = `${baseUrl}/api/generate_canvas?converted=${encodeURIComponent(convertedImage)}${originalImageUrl ? `&original=${encodeURIComponent(originalImageUrl)}` : ''}`
        
        console.log('🔗 [shareToFarcasterFromModal] Calling canvas generation API:', canvasApiUrl)
        
        const canvasResponse = await fetch(canvasApiUrl)
        const canvasData = await canvasResponse.json()
        
        if (!canvasData.success || !canvasData.canvasUrl) {
          throw new Error('Failed to generate canvas')
        }
        
        canvasUrl = canvasData.canvasUrl
        setCanvasCache(canvasUrl) // Cache the generated canvas
        console.log('💾 [shareToFarcasterFromModal] Canvas cached for future use')
      }
      console.log('✅ [handleShare] Canvas generated and uploaded to Cloudinary:', canvasUrl)
      
      // Step 2: Use canvas URL directly for sharing (no separate share page needed)
      console.log('🔗 [handleShare] Using canvas URL directly for sharing:', canvasUrl)
      
      // Create share content with canvas as embed image
      const shareText = `Just transformed my image with Base blue stripe effects using Base It!`
      
      console.log('📝 [handleShare] Share text:', shareText)
      console.log('🖼️ [handleShare] Canvas image URL:', canvasUrl)
      
      // Show user feedback
      console.log('📤 [handleShare] Opening compose dialog...')
      
      // Create Frame URL with canvas as preview image
      const appUrl = window.location.origin
      const frameUrl = `${appUrl}/api/s?canvas=${encodeURIComponent(canvasUrl)}`
      const shareResult = await shareToFarcaster(shareText, frameUrl)
      
      console.log('📊 [shareToFarcasterFromModal] Share result:', shareResult)
      
      if (shareResult.success) {
        console.log('✅ [shareToFarcasterFromModal] Share completed successfully')
      } else {
        console.log('⚠️ [shareToFarcasterFromModal] Share completed with fallback, reason:', shareResult.reason)
      }
      
    } catch (error) {
      console.error('❌ [shareToFarcasterFromModal] Share error:', error)
      console.error('❌ [shareToFarcasterFromModal] Error details:', {
        message: error.message,
        stack: error.stack,
        convertedImage: convertedImage,
        selectedImage: selectedImage?.url
      })
      alert('Failed to share. Please try again.')
    } finally {
      setIsSharing(false)
    }
  }

  const closeShareModal = () => {
    setShowShareModal(false)
  }

  const shareToFarcaster = async (text, url) => {
    // Use Farcaster SDK for compose cast
    try {
      console.log('Attempting to compose cast with text length:', text.length)
      console.log('SDK actions available:', !!sdk.actions)
      console.log('ComposeCast available:', !!sdk.actions.composeCast)
      
      // Simple embeds array with just the Frame URL
      const embeds = [url]
      
      console.log('📤 About to call sdk.actions.composeCast with:')
      console.log('  - text:', text)
      console.log('  - embeds:', embeds)
      
      const result = await sdk.actions.composeCast({
        text: text,
        embeds: embeds
      })
      
      console.log('📥 Compose result:', result)
      console.log('📥 Result type:', typeof result)
      console.log('📥 Result keys:', result ? Object.keys(result) : 'null')
      
      // Handle result
      if (result?.cast) {
        console.log('Cast posted successfully:', result.cast.hash)
        console.log('✅ Share successful! Your cast has been posted.')
        return { success: true, cast: result.cast }
      } else {
        console.log('No compose dialog appeared or user cancelled')
        console.log('❌ Share failed - no dialog shown')
        
        // Try fallback immediately since no dialog appeared
        const farcasterUrl = `https://warpcast.com/~/compose?text=${encodeURIComponent(text)}&embeds[]=${encodeURIComponent(url)}`
        console.log('🔗 Opening fallback URL:', farcasterUrl)
        window.open(farcasterUrl, '_blank')
        return { success: false, reason: 'composeCast returned undefined/null', result: result }
      }
    } catch (error) {
      console.error('Failed to compose cast:', error)
      // Fallback to intent URL
      const farcasterUrl = `https://warpcast.com/~/compose?text=${encodeURIComponent(text)}&embeds[]=${encodeURIComponent(url)}`
      console.log('🔗 Opening fallback URL after error:', farcasterUrl)
      window.open(farcasterUrl, '_blank')
      return { success: false, reason: 'error', error: error.message }
    }
  }

  const handleDownload = async () => {
    if (!convertedImage) {
      console.log('No converted image to copy')
      return
    }

    console.log('Attempting to copy URL:', convertedImage)
    
    try {
      // Copy the Cloudinary URL to clipboard
      await navigator.clipboard.writeText(convertedImage)
      console.log('URL copied successfully to clipboard')
      setUrlCopied(true)
      
      // Scroll to the message after a short delay to ensure it's rendered
      setTimeout(() => {
        const messageElement = document.querySelector('.url-copied-message')
        if (messageElement) {
          messageElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          })
        }
      }, 100)
      
      // Start fade-out after 2.3 seconds (allowing 0.3s for the fade-out animation)
      setTimeout(() => {
        const messageElement = document.querySelector('.url-copied-message')
        if (messageElement) {
          messageElement.classList.add('fade-out')
        }
      }, 2300)
      
      // Remove the message from DOM after fade-out completes
      setTimeout(() => setUrlCopied(false), 2600)
    } catch (error) {
      console.error('Failed to copy URL:', error)
      alert('Failed to copy URL to clipboard. Please try again.')
    }
  }

  const handleMint = async () => {
    if (!convertedImage) {
      alert('Please convert an image first before minting')
      return
    }

    // If wallet not connected, connect first
    if (!isConnected) {
      try {
        // Find the right connector based on environment
        const isInFarcaster = await sdk?.isInMiniApp?.()

        if (isInFarcaster) {
          // Farcaster flow - use mini app connector
          const preferredConnector = connectors.find(c => c.id === 'farcasterMiniApp') || connectors[0]
          await connect({ connector: preferredConnector })
          return // Exit here, user will need to click mint again after connecting
        } else {
          // Non-Farcaster flow - show modal to let user choose
          setShowWalletModal(true)
          return
        }
      } catch (error) {
        console.error('Wallet connection failed:', error)
        alert('Failed to connect wallet. Please try again.')
        return
      }
    }

    if (!canMint) {
      alert(`Insufficient USDC balance. You need ${mintPrice} USDC to mint.`)
      return
    }

    try {
      await mintNFT(convertedImage)
    } catch (error) {
      console.error('Minting failed:', error)
      alert(`Minting failed: ${error.message}`)
    }
  }

  return (
    <div className="image-converter">
      <div className="upload-section">
        <div className="upload-content">
          <div className="upload-text">
            <span>Upload Your</span>
            <span>Image</span>
          </div>
          <div
            className="upload-button"
            onClick={() => fileInputRef.current?.click()}
          >
            <img src="/upload_icon_base_orig.png" alt="Upload" className="upload-icon-img" />
          </div>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />
      </div>

      {selectedImage && (
        <div className="tab-container">
          <div className="tab-header">
            <button 
              className={`tab-button ${activeTab === 'original' ? 'active' : ''}`}
              onClick={() => setActiveTab('original')}
            >
              Original Image
            </button>
            {convertedImage && (
              <button 
                className={`tab-button ${activeTab === 'converted' ? 'active' : ''}`}
                onClick={() => setActiveTab('converted')}
              >
                Based Image
              </button>
            )}
          </div>

          <div className="tab-content">
            {activeTab === 'original' && (
              <div className="preview-section">
                <img 
                  src={selectedImage.url} 
                  alt="Original" 
                  className="image-preview"
                />
              </div>
            )}

            {activeTab === 'converted' && convertedImage && (
              <div className="result-section">
                <img 
                  src={convertedImage} 
                  alt="Converted" 
                  className="image-preview"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="action-section">
          <div className="action-button-container">
            {!convertedImage ? (
              <button
                className="convert-button"
                onClick={handleConvert}
                disabled={isConverting}
              >
                {isConverting ? (
                  <>
                    <div className="base-logo tiny loading">
                      <div className="logo-square"></div>
                    </div>
                    Converting...
                  </>
                ) : (
                  <>
                    base it.
                  </>
                )}
              </button>
            ) : (
              <div className="action-buttons-group">
                <div
                  className="download-button"
                  onClick={handleDownload}
                >
                  <img src="/download_icon_base.png" alt="Copy URL" className="download-icon-img" />
                </div>
                <div
                  className={`mint-button ${isMinting ? 'disabled' : ''}`}
                  onClick={handleMint}
                >
                  <img src="/mintIcon.png" alt="Mint NFT" className="mint-icon-img" />
                </div>
                <button 
                  className="convert-button"
                  onClick={handleShare}
                  disabled={isSharing}
                >
                  {isSharing ? (
                    <>
                      <div className="base-logo tiny loading">
                        <div className="logo-square"></div>
                      </div>
                      Sharing...
                    </>
                  ) : (
                    'Share'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {urlCopied && (
        <div className="url-copied-row">
          <div className="url-copied-message">
            <p>Image URL copied to clipboard!</p>
            <p>You can now open it in any browser to save the image.</p>
          </div>
        </div>
      )}

      {/* Mint Status */}
      {isConnected && convertedImage && (
        <div className="mint-status">
          <div className="mint-info">
            <span>Wallet Balance: {usdcBalance.toFixed(2)} USDC</span>
            <span>Mint Price: {mintPrice} USDC</span>
          </div>
        </div>
      )}

      {/* Mint Result */}
      {mintResult && mintResult.success && (
        <div ref={mintSuccessRef} className="mint-success">
          <div className="success-message">
            <h4>NFT minted successfully!</h4>
            <div className="nft-links">
              {mintResult.transactionHash && (
                <button
                  className="view-transaction"
                  onClick={() => window.open(`https://basescan.org/tx/${mintResult.transactionHash}`, '_blank')}
                >
                  View Transaction
                </button>
              )}
              <button
                className="view-transaction"
                onClick={() => {
                  const contractAddr = CONTRACT_ADDRESSES[chainId] || '0xA3d1EC979f1f1351261c4BeCC1851b8bDb6d3187'
                  window.open(`https://opensea.io/assets/base/${contractAddr}`, '_blank')
                }}
              >
                View on OpenSea
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mint Error */}
      {mintError && (
        <div ref={mintErrorRef} className="mint-error">
          <p>
            <img src="/error_icon.png" alt="Error" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
            Minting failed. Please try again.
          </p>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={closeShareModal}>
          <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share your Based image to</h3>
            </div>
            <div className="share-options">
              <button className="share-option" onClick={shareToX}>
                <img src="/x_logo.svg" alt="X" className="share-option-icon" />
                <span className="share-option-text">X/Twitter</span>
              </button>
              <button className="share-option" onClick={shareToFarcasterFromModal}>
                <img src="/base_square.svg" alt="Base" className="share-option-icon" />
                <span className="share-option-text">Base/Farcaster</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Selection Modal */}
      {showWalletModal && (
        <div className="share-modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Are you on a mobile device?</h3>
              <p>(So we know how to connect your wallet)</p>
            </div>
            <div className="share-options">
              <button 
                className="share-option" 
                onClick={() => handleWalletSelection(true)}
              >
                <span className="share-option-text">Yes</span>
              </button>
              <button 
                className="share-option" 
                onClick={() => handleWalletSelection(false)}
              >
                <span className="share-option-text">No, I'm on a desktop</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageConverter 